import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Save, User } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AdminAccount() {
  const { user, setUser } = useAuth();
  const [accountData, setAccountData] = useState({
    name: '',
    email: '',
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setAccountData(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || ''
      }));
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAccountData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (accountData.new_password && accountData.new_password !== accountData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    if (accountData.new_password && !accountData.current_password) {
      toast.error('Current password is required to set a new one');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: accountData.name,
        email: accountData.email
      };
      if (accountData.new_password) {
        payload.current_password = accountData.current_password;
        payload.new_password = accountData.new_password;
      }

      const response = await axios.put(`${API}/auth/profile`, payload, { withCredentials: true });
      setUser(response.data);
      setAccountData(prev => ({ ...prev, current_password: '', new_password: '', confirm_password: '' }));
      toast.success('Account updated');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to update account');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-8 max-w-2xl" data-testid="admin-account">
      <div className="mb-8">
        <h1 className="text-2xl font-bold tracking-tight">Admin Account</h1>
        <p className="text-muted-foreground">Update your name, email, and password</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="border border-border rounded-lg p-6 space-y-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-primary/10 rounded-lg">
              <User className="h-5 w-5 text-primary" />
            </div>
            <h2 className="text-lg font-semibold">Profile</h2>
          </div>

          <div className="space-y-2">
            <Label htmlFor="account_name">Display Name</Label>
            <Input
              id="account_name"
              name="name"
              value={accountData.name}
              onChange={handleChange}
              placeholder="Your name"
              data-testid="account-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="account_email">Email</Label>
            <Input
              id="account_email"
              name="email"
              type="email"
              value={accountData.email}
              onChange={handleChange}
              placeholder="admin@example.com"
              data-testid="account-email-input"
            />
          </div>
        </div>

        <div className="border border-border rounded-lg p-6 space-y-6">
          <h2 className="text-lg font-semibold">Change Password</h2>
          <p className="text-sm text-muted-foreground">Leave blank to keep your current password.</p>

          <div className="space-y-2">
            <Label htmlFor="current_password">Current Password</Label>
            <Input
              id="current_password"
              name="current_password"
              type="password"
              value={accountData.current_password}
              onChange={handleChange}
              placeholder="Enter current password"
              data-testid="account-current-password"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="new_password">New Password</Label>
              <Input
                id="new_password"
                name="new_password"
                type="password"
                value={accountData.new_password}
                onChange={handleChange}
                placeholder="Enter new password"
                data-testid="account-new-password"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm_password">Confirm Password</Label>
              <Input
                id="confirm_password"
                name="confirm_password"
                type="password"
                value={accountData.confirm_password}
                onChange={handleChange}
                placeholder="Confirm new password"
                data-testid="account-confirm-password"
              />
            </div>
          </div>
        </div>

        <Button type="submit" disabled={saving} data-testid="save-account-btn">
          <Save className="mr-2 h-4 w-4" />
          {saving ? 'Saving...' : 'Update Account'}
        </Button>
      </form>
    </div>
  );
}
